import { Button, Card } from '@any-ui-react/core';
import { useCallback } from 'react';
import { CardSection } from '@/common/components/shared/card/CardSection';
import { useDeleteRecordModal } from '@/common/components/shared/delete-record-modal/useDeleteRecordModal';
import { EmployeeLink } from '@/common/components/shared/link/EmployeeLink';
import { toast } from '@/common/components/shared/toaster/toast';
import {
  type GetManagerQuery,
  type GetPendingManagerChangeReservationQuery,
  useCancelManagerChangeReservationMutation,
} from '@/common/graphql';
import { useEmployeeAccess } from '@/common/hooks/employees/useEmployeeAccess';
import { ApiUtils, DATETIME_FORMATS, DateUtils } from '@/common/utils';
import { ManagerInfo } from './ManagerInfo';

interface Props {
  primaryManager: GetManagerQuery['employee'] | undefined;
  dottedLineManager: GetManagerQuery['employee'] | undefined;
  reservation: NonNullable<GetPendingManagerChangeReservationQuery['pendingManagerChangeReservation']>;
  refetch: () => Promise<any>;
}

export const PendingManagerChanges = ({ primaryManager, dottedLineManager, reservation, refetch }: Props) => {
  const { hasEmployeeGeneralInformationEditAccess } = useEmployeeAccess();
  const { openDeleteRecordModal } = useDeleteRecordModal();

  const [cancelReservation, { loading: cancelling }] = useCancelManagerChangeReservationMutation();

  const handleCancel = useCallback(() => {
    openDeleteRecordModal({
      title: 'Are you sure you want to cancel this reservation?',
      successButtonText: 'Yes',
      onSuccess: async () => {
        try {
          await cancelReservation({ variables: { id: reservation.id } });
          toast.success('Manager change reservation cancelled.');
          await refetch();
        } catch (error) {
          ApiUtils.handleApiError(error as Parameters<typeof ApiUtils.handleApiError>[0]);
        }
      },
    });
  }, [openDeleteRecordModal, cancelReservation, reservation, refetch]);

  return (
    <Card className="col-span-12" withBorder={true}>
      <div className="col-span-2 font-semibold mb-4 flex flex-wrap items-center justify-between">
        <span>
          Pending Manager Change — on&nbsp;
          {DateUtils.formatDate(reservation.effectiveDate, DATETIME_FORMATS.MMMddyyyy)}
        </span>
        {hasEmployeeGeneralInformationEditAccess && (
          <Button color="red" disabled={cancelling} size="xs" variant="outline" onClick={handleCancel}>
            Revert changes
          </Button>
        )}
      </div>
      <CardSection>
        <div className="flex flex-col gap-3">
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="mb-1">Primary Manager</div>
              <PendingManager currentData={primaryManager} pendingData={reservation.primaryManager} />
            </div>
            <div>
              <div className="mb-1">Dotted Line Manager</div>
              <PendingManager currentData={dottedLineManager} pendingData={reservation.dottedLineManager} />
            </div>
          </div>
        </div>
      </CardSection>
    </Card>
  );
};

interface PendingManagerProps {
  currentData: GetManagerQuery['employee'] | undefined;
  pendingData:
    | NonNullable<GetPendingManagerChangeReservationQuery['pendingManagerChangeReservation']>['primaryManager']
    | NonNullable<GetPendingManagerChangeReservationQuery['pendingManagerChangeReservation']>['dottedLineManager']
    | undefined;
}

const PendingManager = ({ currentData, pendingData }: PendingManagerProps) => {
  return !pendingData?.id ? (
    <>
      <EmployeeLink employeeId={currentData?.id}>{currentData?.generalInformation?.preferredFullName}</EmployeeLink>
      <span className="text-gray-7">&nbsp;(No change)</span>
      {currentData && <ManagerInfo managerInfo={currentData} showLink={false} />}
    </>
  ) : (
    <>
      {currentData ? (
        <EmployeeLink employeeId={currentData.id}>{currentData.generalInformation.preferredFullName}</EmployeeLink>
      ) : (
        <span className="text-gray-7">(Not set)</span>
      )}
      {pendingData.id !== currentData?.id ? (
        <>
          &nbsp;→&nbsp;
          <EmployeeLink employeeId={pendingData.id}>{pendingData.generalInformation.preferredFullName}</EmployeeLink>
        </>
      ) : (
        <span className="text-gray-7">&nbsp;(No change)</span>
      )}
      {pendingData && <ManagerInfo managerInfo={pendingData} showLink={false} />}
    </>
  );
};
